'use client';
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import MuxPlayer from '@mux/mux-player-react';

interface Media {
  url: string;
  alternativeText?: string;
  name: string;
  description: string;
}

interface ColumnMediaItem {
  desktop_media?: Media[];
  mobile_media?: Media[];
  name: string;
  description: string;
  item: string;
  index: any;
  mux_video: any;
  enable_mux_video: boolean;
}

interface ComponentSections2ColumnMediaProps {
  data: {
    ColumnMedia: ColumnMediaItem[];
    name: string;
    description: string;
    __typename: string;
    padding: {
      mobile_top_padding: any;
      mobile_bottom_padding: any;
      desktop_top_padding: any;
      desktop_bottom_padding: any;
    };
  };
}

const ComponentSections2ColumnMedia: React.FC<ComponentSections2ColumnMediaProps> = ({ data }) => {
  const [top, setTop] = useState('');
  const [bottom, setBottom] = useState('');
  useEffect(() => {
    const updatePadding = () => {
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        setTop(data.padding?.mobile_top_padding ?? '');
        setBottom(data.padding?.mobile_bottom_padding ?? '');
      } else {
        setTop(data.padding?.desktop_top_padding ?? '');
        setBottom(data.padding?.desktop_bottom_padding ?? '');
      }
    };

    updatePadding(); // Run on mount

    window.addEventListener('resize', updatePadding); // Listen on resize
    return () => window.removeEventListener('resize', updatePadding); // Clean up
  }, [data]);
  return (
    <>
      <section
        id={data?.__typename}
        style={{
          paddingTop: `${top}rem`,
          paddingBottom: `${bottom}rem`,
        }}
        className='pb-10 md:pb-24'
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-3 list-project-box">
          {data?.ColumnMedia.map((item: ColumnMediaItem, index: number) => {
            const muxPlaybackId = item?.mux_video?.playback_id;
            const enableMuxVideo = item.enable_mux_video;
            return (
              <motion.div
                key={index}
                className="w-full list-project-content block"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                viewport={{ once: true, amount: 0.2 }}
              >
                {enableMuxVideo && muxPlaybackId ? (
                  <MuxPlayer
                    playbackId={muxPlaybackId}
                    autoPlay
                    muted
                    loop
                    playsInline
                    streamType="on-demand"
                    className="h-full"
                    style={{ width: '100%' }}
                  />
                ) : (
                  <>
                    {/* Desktop Image (for md and up) */}
                    {item.desktop_media?.[0]?.url && (
                      <>
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={item.desktop_media[0].url}
                          alt={item.desktop_media[0].alternativeText || 'Desktop Image'}
                          width={800}
                          height={500}
                          className="hidden md:block w-full h-auto"
                        />
                      </>
                    )}

                    {/* Mobile Image fallback to Desktop */}
                    {item.mobile_media?.[0]?.url ? (
                      <>
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={item.mobile_media[0].url}
                          alt={item.mobile_media[0].alternativeText || 'Mobile Image'}
                          className="block md:hidden w-full h-auto"
                        />
                      </>
                    ) : item.desktop_media?.[0]?.url ? (
                      <>
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={item.desktop_media[0].url}
                          alt={item.desktop_media[0].alternativeText || 'Desktop Image Fallback'}
                          className="block md:hidden w-full object-cover"
                        />
                      </>
                    ) : null}
                  </>
                )}
                {/* Desktop Image */}
              </motion.div>
            );
          })}
        </div>
      </section>
    </>
  );
};

export default ComponentSections2ColumnMedia;
